import clsx from "clsx";
import {
  AlertCircle,
  CheckCircle2,
  CircleDollarSign,
  LoaderCircle,
  ReceiptText,
  Trash2,
  X,
} from "lucide-react";

import { money, odds } from "@/components/ui";

import type { BetSlipLeg } from "./BetSlipProvider";
import type { BetSlipErrorView } from "./presentation";
import { displayRaceLabel, MIN_STAKE_CENTS } from "./presentation";

type CouponType = "single" | "combined" | "system";

type QuotedLeg = {
  raceId: string;
  betType: string;
  sel: number[];
  oddsH: number;
  bettingOpen: boolean;
};

const QUICK_STAKES_CENTS = [500, 1_000, 2_500, 5_000] as const;

const MARKET_LABELS: Record<BetSlipLeg["betType"], string> = {
  win: "Win",
  place: "Place",
  show: "Show",
  exacta: "Exacta",
  quinella: "Quinella",
  trifecta: "Trifecta",
};

export function BetSlipModeTabs({
  couponType,
  legCount,
  onChange,
}: {
  couponType: CouponType;
  legCount: number;
  onChange: (couponType: CouponType, systemK?: number) => void;
}) {
  return (
    <div aria-label="Bet type" className="betslip-type-tabs" role="tablist">
      <button
        aria-controls="betslip-builder-panel"
        aria-selected={couponType === "single"}
        className={clsx(couponType === "single" && "is-active")}
        disabled={legCount !== 1}
        id="betslip-tab-single"
        onClick={() => onChange("single")}
        role="tab"
        type="button"
      >
        Single
      </button>
      <button
        aria-controls="betslip-builder-panel"
        aria-selected={couponType === "combined"}
        className={clsx(couponType === "combined" && "is-active")}
        disabled={legCount < 2}
        id="betslip-tab-combined"
        onClick={() => onChange("combined")}
        role="tab"
        type="button"
      >
        Multi
      </button>
      <button
        aria-controls="betslip-builder-panel"
        aria-selected={couponType === "system"}
        className={clsx(couponType === "system" && "is-active")}
        disabled={legCount < 3}
        id="betslip-tab-system"
        onClick={() => onChange("system", Math.max(1, legCount - 1))}
        role="tab"
        type="button"
      >
        System
      </button>
    </div>
  );
}

export function SystemSizeField({
  legCount,
  systemK,
  onChange,
}: {
  legCount: number;
  systemK: number | null;
  onChange: (size: number) => void;
}) {
  return (
    <label className="betslip-system-field">
      <span>Combination size</span>
      <select
        aria-label="System combination size"
        onChange={(event) => onChange(Number(event.target.value))}
        value={systemK ?? 1}
      >
        {Array.from(
          { length: Math.max(1, legCount - 1) },
          (_, index) => index + 1,
        ).map((size) => (
          <option key={size} value={size}>
            {size} of {legCount}
          </option>
        ))}
      </select>
    </label>
  );
}

export function BetSlipSelectionList({
  legs,
  quotedLegs,
  onRemove,
}: {
  legs: BetSlipLeg[];
  quotedLegs?: QuotedLeg[];
  onRemove: (key: string) => void;
}) {
  return (
    <div aria-label="Selections" className="betslip-legs" role="list">
      {legs.map((leg) => {
        const quoted = quotedLegs?.find(
          (candidate) =>
            candidate.raceId === leg.raceId &&
            candidate.betType === leg.betType &&
            candidate.sel.join(",") === leg.sel.join(","),
        );
        const currentOdds = quoted?.oddsH ?? leg.oddsH;
        const changed = quoted !== undefined && quoted.oddsH !== leg.oddsH;
        const closed = quoted?.bettingOpen === false;
        return (
          <article
            className={clsx("betslip-leg", closed && "is-closed")}
            key={leg.key}
            role="listitem"
          >
            <button
              aria-label={`Remove ${leg.selectionLabel}`}
              className="betslip-leg__remove"
              onClick={() => onRemove(leg.key)}
              type="button"
            >
              <X aria-hidden />
            </button>
            <div className="betslip-leg__copy">
              <span className="betslip-leg__market">
                {MARKET_LABELS[leg.betType]}
              </span>
              <strong>{leg.selectionLabel}</strong>
              <span className="betslip-leg__race">
                {displayRaceLabel(leg.raceLabel)}
              </span>
            </div>
            <div className="betslip-leg__price">
              <span>Odds</span>
              <strong>{odds(currentOdds)}</strong>
              {closed ? (
                <span className="betslip-leg__closed">Closed</span>
              ) : changed ? (
                <span className="betslip-leg__updated">Updated</span>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

export function BetSlipSummary({
  currency,
  inputValid,
  lines,
  onInputChange,
  onQuickStake,
  potentialCents,
  quoteLoading,
  stakeCents,
  stakeInput,
}: {
  currency: string;
  inputValid: boolean;
  lines?: number;
  onInputChange: (value: string) => void;
  onQuickStake: (cents: number) => void;
  potentialCents?: number;
  quoteLoading: boolean;
  stakeCents: number;
  stakeInput: string;
}) {
  const stakeMessage = !inputValid
    ? "Enter a valid amount with no more than two decimal places."
    : stakeCents < MIN_STAKE_CENTS
      ? `Minimum stake is ${money(MIN_STAKE_CENTS, currency)}.`
      : "";
  const describedBy = stakeMessage ? "betslip-stake-message" : undefined;

  return (
    <section
      aria-labelledby="betslip-summary-title"
      className="betslip-summary"
    >
      <div className="betslip-summary__heading">
        <div>
          <span>Wager details</span>
          <h3 id="betslip-summary-title">Stake and return</h3>
        </div>
        <CircleDollarSign aria-hidden />
      </div>

      <div className="betslip-stake">
        <label htmlFor="betslip-stake">Total stake</label>
        <div className="betslip-stake__input">
          <span aria-hidden>{currencySymbol(currency)}</span>
          <input
            aria-describedby={describedBy}
            aria-invalid={Boolean(stakeMessage)}
            autoComplete="off"
            id="betslip-stake"
            inputMode="decimal"
            onChange={(event) => onInputChange(event.target.value)}
            spellCheck={false}
            type="text"
            value={stakeInput}
          />
          <span>{currency}</span>
        </div>
        <div aria-label="Quick stake" className="betslip-stake__quick">
          {QUICK_STAKES_CENTS.map((cents) => (
            <button
              aria-pressed={stakeCents === cents}
              key={cents}
              onClick={() => onQuickStake(cents)}
              type="button"
            >
              {compactMoney(cents, currency)}
            </button>
          ))}
        </div>
        {stakeMessage ? (
          <p className="betslip-stake__message" id="betslip-stake-message">
            {stakeMessage}
          </p>
        ) : null}
      </div>

      <dl className="betslip-totals">
        <div>
          <dt>Potential return</dt>
          <dd aria-live="polite">
            {quoteLoading ? (
              <span className="betslip-totals__loading">
                <LoaderCircle aria-hidden />
                Updating
              </span>
            ) : potentialCents === undefined || stakeMessage ? (
              "—"
            ) : (
              money(potentialCents, currency)
            )}
          </dd>
        </div>
        {lines && lines > 1 ? (
          <div>
            <dt>System lines</dt>
            <dd>{lines}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

export function BetSlipErrorNotice({ error }: { error: BetSlipErrorView }) {
  return (
    <div
      className={clsx("betslip-notice", `is-${error.kind}`)}
      data-error-code={error.code}
      role="alert"
    >
      <AlertCircle aria-hidden />
      <p>{error.message}</p>
      <span className="betslip-visually-hidden">Error code {error.code}</span>
    </div>
  );
}

export function BetSlipGateNotice({
  code,
  message,
}: {
  code: string;
  message: string;
}) {
  return (
    <div
      className="betslip-notice is-capability"
      data-error-code={code}
      role="status"
    >
      <AlertCircle aria-hidden />
      <p>{message}</p>
      <span className="betslip-visually-hidden">Status code {code}</span>
    </div>
  );
}

export function BetSlipEmpty() {
  return (
    <div className="betslip-empty">
      <span aria-hidden>
        <ReceiptText />
      </span>
      <h3>Your betslip is empty</h3>
      <p>Select Win, Place, Show or an exotic from any open race.</p>
    </div>
  );
}

export function BetSlipSuccess({
  pending,
  onDone,
}: {
  pending: boolean;
  onDone: () => void;
}) {
  return (
    <div className="betslip-placement" role="status">
      <span aria-hidden>
        <CheckCircle2 />
      </span>
      <p>{pending ? "Wager submitted" : "Wager confirmed"}</p>
      <h3>{pending ? "Bet submitted" : "Bet accepted"}</h3>
      <p>
        {pending
          ? "We are confirming your wager. Check your wager history for the latest status."
          : "Your wager is open and ready to follow in your wager history."}
      </p>
      <button
        className="betslip-placement__done"
        onClick={onDone}
        type="button"
      >
        Done
      </button>
    </div>
  );
}

export function BetSlipClearButton({ onClear }: { onClear: () => void }) {
  return (
    <button className="betslip-clear" onClick={onClear} type="button">
      <Trash2 aria-hidden />
      Clear betslip
    </button>
  );
}

function compactMoney(cents: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function currencySymbol(currency: string) {
  return (
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? currency
  );
}
